import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../data/constants';
import { getPutDownMillLookupByItem } from '../../api/ebs-mfg-api/index.js';
import { useApiReady } from '@pella/fetch-client-react';

/**
 * Hook to fetch the put down mill lot data from ebs-mfg given an item number.
 * Returns a normalized shape: { data, isPending, error }. Unlike the other
 * action-query lookups, a no-lots response ([{ lotNumber: null, ... }]) is a
 * valid success and must NOT set an error — only a true API failure does, so
 * the request is still created and the empty result persisted.
 * The endpoint hard codes Org 63 (Metal Plant) server-side; no org is passed.
 * @param {object} input
 * @param {string | undefined} input.item
 * @param {boolean} [input.enabled] - Optional override; when false the query is disabled regardless of other params
 */
const usePutDownMillLookup = ({ item, enabled: enabledOverride = true }) => {
  const { apiReady } = useApiReady();
  const query = useQuery({
    queryKey: [QUERY_KEYS.PUT_DOWN_MILL_BY_ITEM, item],
    queryFn: () =>
      getPutDownMillLookupByItem({
        item: /** @type {string} */ (item),
      }),
    enabled: Boolean(enabledOverride && apiReady && item),
  });

  const error = query.isError ? 'Unable to retrieve lot data for the entered item number. Please try again.' : null;

  return {
    data: query.data,
    isPending: query.isPending,
    error,
  };
};

export { usePutDownMillLookup };
